import { Checkbox, Skeleton, Stack } from '@atgfr8/atg-ui-tailwind';
import { BasicTable, Notification, ToastType } from '@components';
import { AtgLoader } from '@components/AtgLoader';
import CommonFileUpload, {
  CommonFileUploadFiles,
} from '@components/FileUpload/CommonFileUpload';
import { useParams } from '@solidjs/router';
import {
  ediConfigurationStore,
  getEDIConfigIsTenderAutoResponseAuto990Decline,
  getUploadedAutoAcceptDeclineSheet,
  setEdiConfigurationStore,
  TenderAutoAcceptDeclineRow,
  toggleAutoAcceptDecline,
  uploadAutoAcceptDeclineSheet,
  UploadedAutoAcceptDeclineSheet,
} from '@store/customers/customerDetails';
import { handleToast, isFullAdmin } from '@utils/utils';
import { createSignal, onMount, Show } from 'solid-js';

const TenderAutoResponse = () => {
  const [tenderAutoResponse, setTenderAutoResponse] = createSignal(false);
  const [
    uploadedAutoAcceptDeclienSheetData,
    setUploadedAutoAcceptDeclienSheetData,
  ] = createSignal<UploadedAutoAcceptDeclineSheet | null>(null);
  const [file, setFile] = createSignal<CommonFileUploadFiles[] | null>(null);
  const [loading, setLoading] = createSignal(true);
  const [removeWarning, setRemoveWarning] = createSignal(true);
  const [tenderResponseToggleLoading, setTenderResponseToggleLoading] =
    createSignal(false);

  const params = useParams();
  const columns = [
    {
      name: 'Origin City',
      key: 'originCity',
      minWidth: 130,
    },
    {
      name: 'Origin State',
      key: 'originState',
      minWidth: 110,
    },
    {
      name: 'Origin Zip',
      key: 'originZip',
      minWidth: 110,
    },
    {
      name: 'Destination City',
      key: 'destinationCity',
      minWidth: 140,
    },
    {
      name: 'Destination State',
      key: 'destinationState',
      minWidth: 150,
    },
    {
      name: 'Destination Zip',
      key: 'destinationZip',
      minWidth: 140,
    },
    {
      name: 'Auto Accept',
      key: 'autoAccept',
      renderCell: (row: TenderAutoAcceptDeclineRow) =>
        row.autoAccept ? 'true' : 'false',
      minWidth: 120,
    },
  ];

  onMount(async () => {
    setLoading(true);
    const [res, res1] = await Promise.all([
      getEDIConfigIsTenderAutoResponseAuto990Decline(params.id as string),
      getUploadedAutoAcceptDeclineSheet(params.id as string),
    ]);
    if (res) {
      setTenderAutoResponse(res.value);
    }
    if (res1) {
      setUploadedAutoAcceptDeclienSheetData(res1);
    }
    setLoading(false);
  });

  const handleFileChange = async (file: File) => {
    try {
      const res = await uploadAutoAcceptDeclineSheet(params.id as string, file);
      if (res && res.isSuccess) {
        setEdiConfigurationStore('errorMessage', '');
        handleToast(ToastType.Success, 'File uploaded successfully');
        const sheetData = await getUploadedAutoAcceptDeclineSheet(
          params.id as string,
        );
        if (sheetData) {
          setUploadedAutoAcceptDeclienSheetData(sheetData);
        }
      }
    } finally {
      setFile(null);
    }
  };

  const handleToggleAutoAcceptDecline = async (checked: boolean) => {
    setTenderResponseToggleLoading(true);
    try {
      const res = await toggleAutoAcceptDecline(params.id as string, checked);
      const status = checked ? 'enabled' : 'disabled';
      if (res) {
        if (res.isSuccess) {
          handleToast(
            ToastType.Success,
            `Auto accept decline ${status} successfully.`,
          );
          setTenderAutoResponse(checked);
        }
      }
    } finally {
      setTenderResponseToggleLoading(false);
    }
  };

  return (
    <Show
      when={!loading()}
      fallback={
        <Stack pt={2}>
          <Skeleton height={60} variant="rectangular" class="mb-2" />
          <Skeleton
            height={60}
            width={'200px'}
            variant="rectangular"
            class="mb-2"
          />
          <Skeleton height={60} variant="rectangular" class="mb-2" />
          <Skeleton height={120} variant="rectangular" class="mb-2" />
        </Stack>
      }
    >
      <Stack spacing={2} pt={2}>
        <Show when={tenderResponseToggleLoading()}>
          <AtgLoader />
        </Show>
        <Show when={ediConfigurationStore.errorMessage}>
          <Notification
            type="error"
            text={ediConfigurationStore.errorMessage}
          />
        </Show>
        <Show when={removeWarning()}>
          <Notification
            onClose={() => {
              setRemoveWarning(false);
            }}
            type="warning"
            text=" Make sure your upload lanes are correct. Failure to do so could result in a loss of business, or unwanted business that you will be required to honor."
          />
        </Show>
        <Checkbox
          size="xs"
          color="info"
          disabled={tenderResponseToggleLoading()}
          class="ml-[-9px] bg-[#D32F2F4D] w-fit pr-3 rounded-md border border-[#B00020] m-0"
          label="Enable Tender Auto Response"
          checked={tenderAutoResponse()}
          onChange={async (e) => {
            await handleToggleAutoAcceptDecline(e);
          }}
          dataTestId="tender-auto-response-enable-checkboxInput"
        />
        <CommonFileUpload
          height={1}
          isMultipleUpload
          maxFileSize={2097152}
          uploadedFiles={file() ?? []}
          shouldUploadOnSelect
          acceptedFileTypes={['csv']}
          isRemoveFile
          apiCallBackFn={handleFileChange}
          isAdmin={isFullAdmin()}
        />
        <BasicTable
          columns={columns}
          rows={
            uploadedAutoAcceptDeclienSheetData()?.tenderAutoResponseRows || []
          }
        />
      </Stack>
    </Show>
  );
};

export default TenderAutoResponse;
